import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Camera, RotateCw, ZoomIn, ZoomOut, Play, User, Trophy, Camera as CameraIcon, ChevronLeft, ChevronRight, Pause, RotateCcw } from 'lucide-react';
import FinalRankingBoard from './FinalRankingBoard';
import * as THREE from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { MTLLoader } from 'three/examples/jsm/loaders/MTLLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader';
import jsQR from 'jsqr';

const GameContainer = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background: black;
  user-select: none;
  cursor: ${props => props.$isDragging ? 'grabbing' : (props.$arObjectVisible ? 'grab' : 'default')};
`;

const CameraFeed = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;

  /* 후면 카메라 사용 시 반전 불필요 */
  transform: scaleX(1);
`;

const VirtualBackground = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #1e3a8a 0%, #7c3aed 50%, #3730a3 100%);

  /* 데스크톱: 불투명도 0.8 */
  opacity: 0.8;

  /* 모바일/태블릿: 완전 제거 */
  @media (max-width: 2560px) {
    display: none;
  }
`;

const HiddenCanvas = styled.canvas`
  display: none;
`;

const ScanOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.isVisible ? 'auto' : 'none'};
`;

const ScanBox = styled.div`
  position: relative;
  width: 90vw;
  height: 90vw;
  max-width: 90vh;
  max-height: 90vh;
  border: 4px solid white;
  border-radius: 16px;
  animation: ${props => props.isScanning ? 'scan-pulse 1s infinite' : 'none'};

  @keyframes scan-pulse {
    0%, 100% { border-color: white; transform: scale(1); }
    50% { border-color: #00ff00; transform: scale(1.05); }
  }
`;

const ScanLine = styled.div`
  position: absolute;
  top: 0;
  width: 4px;
  height: 100%;
  background: #ef4444;
  animation: ${props => props.isScanning ? 'scan-line 2s infinite linear' : 'none'};
  
  @keyframes scan-line {
    0% { left: -120px; }
    100% { left: 120px; }
  }
`;

const ScanText = styled.p`
  color: white;
  text-align: center;
  margin-top: 16px;
  font-size: 16px;
`;

const ARObject = styled.div`
  position: absolute;
  top: 50%;
  left: ${props => props.shiftLeft ? '30%' : '50%'};
  transform: translate(-50%, -50%) scale(${props => props.scale});
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: left 0.3s ease, opacity 0.8s ease;
  animation: ${props => props.isVisible ? 'ar-float 3s ease-in-out infinite' : 'none'};

  @keyframes ar-float {
    0%, 100% { transform: translate(-50%, -50%) scale(${props => props.scale}) translateY(0px); }
    50% { transform: translate(-50%, -50%) scale(${props => props.scale}) translateY(-8px); }
  }

  @media (max-width: 768px) {
    left: 50% !important;
  }
`;

const Cube = styled.div`
  width: 160px;
  height: 160px;
  position: relative;
  transform-style: preserve-3d;
  transform: rotateX(${props => props.rotation.x}deg) rotateY(${props => props.rotation.y}deg) rotateZ(${props => props.rotation.z}deg);
  filter: drop-shadow(0 15px 30px rgba(0,0,0,0.6));
`;

const CubeFace = styled.div`
  position: absolute;
  inset: 0;
  border: 4px solid white;
  box-shadow: 0 0 20px rgba(0,0,0,0.6);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-shadow: 0 2px 4px rgba(0,0,0,0.8);
  font-weight: bold;
`;

const FrontFace = styled(CubeFace)`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  transform: translateZ(40px);
`;

const BackFace = styled(CubeFace)`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  transform: translateZ(-40px) rotateY(180deg);
`;

const LeftFace = styled(CubeFace)`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  transform: rotateY(-90deg) translateZ(40px);
`;

const RightFace = styled(CubeFace)`
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  transform: rotateY(90deg) translateZ(40px);
`;

const TopFace = styled(CubeFace)`
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  transform: rotateX(90deg) translateZ(40px);
`;

const BottomFace = styled(CubeFace)`
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
  transform: rotateX(-90deg) translateZ(40px);
`;

const FaceArrow = styled.div`
  font-size: 32px;
  margin-bottom: 4px;
`;

const FaceLabel = styled.div`
  font-size: 14px;
  font-weight: bold;
`;

const Shadow = styled.div`
  position: absolute;
  top: 192px;
  left: 50%;
  transform: translateX(-50%);
  width: 128px;
  height: 32px;
  background: rgba(0,0,0,0.4);
  border-radius: 50%;
  filter: blur(8px);
  animation: ${props => props.isVisible ? 'shadow-pulse 3s ease-in-out infinite' : 'none'};
  
  @keyframes shadow-pulse {
    0%, 100% { transform: translateX(-50%) scaleX(1); opacity: 0.4; }
    50% { transform: translateX(-50%) scaleX(1.2); opacity: 0.2; }
  }
`;

const RotationDisplay = styled.div`
  position: absolute;
  top: -64px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  white-space: nowrap;
`;

const QuizTitleBanner = styled.div`
  position: absolute;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(37, 99, 235, 0.9);
  backdrop-filter: blur(8px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 12px 24px;
  border-radius: 16px;
  font-size: 18px;
  font-weight: 700;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 100;
  max-width: 80%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserInfoCard = styled.div`
  position: absolute;
  top: 72px;
  left: 16px;
  right: 16px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const UserInfo = styled.div`
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 12px;
  border-radius: 12px;
`;

const UserName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const UserStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
`;

const RankBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #facc15;
  border: 1px solid #facc15;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const ScoreDisplay = styled.div`
  font-size: 14px;
  color: #60a5fa;
`;

const CaptureButton = styled.button`
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s ease;
  
  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const DragHint = styled.div`
  position: absolute;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
  animation: ${props => props.$isVisible && !props.$isDragging ? 'pulse 2s infinite' : 'none'};
  
  @keyframes pulse {
    0%, 100% { opacity: 0.8; }
    50% { opacity: 0.4; }
  }
`;

const DragStatus = styled.div`
  position: absolute;
  top: 64px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(34, 197, 94, 0.8);
  backdrop-filter: blur(4px);
  border-radius: 8px;
  padding: 8px 12px;
  color: white;
  font-size: 12px;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const ScanButton = styled.button`
  position: absolute;
  bottom: 128px;
  left: 50%;
  transform: translateX(-50%);
  background: #2563eb;
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 9999px;
  box-shadow: 0 20px 40px rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1d4ed8;
    transform: translateX(-50%) scale(1.05);
  }
  
  &:active {
    transform: translateX(-50%) scale(0.95);
  }
`;

const ControlPanel = styled.div`
  position: absolute;
  bottom: 96px;
  left: 16px;
  background: rgba(0,0,0,0.8);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.3);
  padding: 16px;
  border-radius: 16px;
`;

const ControlTitle = styled.h3`
  color: white;
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
`;

const AxisControl = styled.div`
  margin-bottom: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const AxisLabel = styled.p`
  color: rgba(255,255,255,0.8);
  font-size: 12px;
  margin: 0 0 4px 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
`;

const RotateButton = styled.button`
  flex: 1;
  background: ${props => props.$color || 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)'};
  color: white;
  border: 1px solid rgba(255,255,255,0.4);
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);

  &:hover {
    background: ${props => props.$hoverColor || 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ResetButton = styled.button`
  width: 100%;
  margin-top: 12px;
  background: linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%);
  color: white;
  border: 1px solid rgba(255,255,255,0.4);
  padding: 10px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ActionPanel = styled.div`
  position: absolute;
  bottom: 96px;
  right: 16px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  padding: 16px;
  border-radius: 16px;
`;

const ActionTitle = styled.h3`
  color: white;
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
`;

const ScaleButton = styled.button`
  flex: 1;
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
  color: white;
  border: 1px solid rgba(255,255,255,0.4);
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;

  &:hover {
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

const QuizButton = styled.button`
  width: 100%;
  background: ${props => props.disabled
    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
    : 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'};
  color: white;
  border: 1px solid rgba(255,255,255,0.4);
  padding: 12px 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  transition: all 0.2s ease;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin-bottom: 12px;
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const ObjectNavigationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const ObjectNavButton = styled.button`
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: rgba(255,255,255,0.3);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ObjectInfo = styled.div`
  flex: 1;
  text-center;
  color: white;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
`;

const ObjectName = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
`;

const ObjectCounter = styled.div`
  opacity: 0.8;
`;

// 퀴즈 모달 스타일 컴포넌트들
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  z-index: 1000;
  opacity: ${props => props.$isOpen ? 1 : 0};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px 0 0 16px;
  width: 50%;
  max-width: 800px;
  min-width: 500px;
  max-height: 100vh;
  height: 100vh;
  overflow-y: auto;
  position: relative;
  transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.3s ease;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.2);

  @media (max-width: 1024px) {
    width: 60%;
    min-width: 400px;
  }

  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    min-width: 100%;
    border-radius: 0;
  }
`;

const ModalHeader = styled.div`
  padding: 24px 24px 16px 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #111827;
`;

const ModalDescription = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 24px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #2563eb;
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.$progress}%;
`;

const TimerDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: ${props =>
    props.isLow ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
    props.isMedium ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' :
    'linear-gradient(135deg, #10b981 0%, #059669 100%)'
  };
  border-radius: 12px;
  margin-bottom: 20px;
  color: white;
  font-size: 24px;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: ${props => props.isLow ? 'pulse 1s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
`;

const TimerIcon = styled.span`
  font-size: 28px;
`;

const QuestionCard = styled.div`
  background-color: #f9fafb;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
`;

const QuestionTitle = styled.h4`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 16px 0;
  color: #111827;
  line-height: 1.5;
`;

const AnswerOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const AnswerOption = styled.button`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.selected ? '#2563eb' : '#e5e7eb'};
  background-color: ${props => props.selected ? '#eff6ff' : 'white'};
  color: ${props => props.selected ? '#2563eb' : '#374151'};
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #2563eb;
    background-color: #f8fafc;
  }
`;

const QuestionPoints = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const PrimaryButton = styled.button`
  flex: 1;
  background-color: ${props => props.variant === 'green' ? '#16a34a' : '#2563eb'};
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:disabled {
    background-color: #9ca3af;
    cursor: not-allowed;
  }
  
  &:hover:not(:disabled) {
    background-color: ${props => props.variant === 'green' ? '#15803d' : '#1d4ed8'};
  }
`;

const SecondaryButton = styled.button`
  flex: 1;
  background-color: white;
  color: #374151;
  border: 1px solid #d1d5db;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #f9fafb;
    border-color: #9ca3af;
  }
`;

const ResultContainer = styled.div`
  text-align: center;
`;

const CelebrationEmoji = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
`;

const ResultTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 24px 0;
  color: #111827;
`;

const ScoreCard = styled.div`
  background: linear-gradient(135deg, #dbeafe 0%, #dcfce7 100%);
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 20px;
`;

const ScoreRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ScoreLabel = styled.span`
  font-size: 14px;
  color: #374151;
`;

const ScoreValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.color || '#111827'};
`;

const ResultsList = styled.div`
  max-height: 160px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const ResultsTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #111827;
`;

const ResultItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 12px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ResultIcon = styled.span`
  font-weight: 600;
  color: ${props => props.$correct ? '#16a34a' : '#dc2626'};
`;

const ThreeCanvas = styled.canvas`
  position: absolute;
  top: 50%;
  left: ${props => props.$shiftLeft ? '30%' : '50%'};
  transform: translate(-50%, -50%);
  width: 90vw !important;
  height: 90vh !important;
  max-width: 1200px;
  max-height: 1200px;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: left 0.3s ease, opacity 0.8s ease;
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
  cursor: ${props => props.$isVisible ? 'grab' : 'default'};

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 768px) {
    left: 50% !important;
  }
`;

const UploadButton = styled.label`
  position: absolute;
  top: 180px;
  left: 16px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 12px 16px;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  z-index: 10;

  &:hover {
    background: rgba(255,255,255,0.2);
  }

  input {
    display: none;
  }
`;

const FileInfo = styled.div`
  position: absolute;
  top: 235px;
  left: 16px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  max-width: 200px;
  z-index: 9;

  input {
    display: none;
  }
`;

const AnimationControls = styled.div`
  position: absolute;
  top: 250px;
  left: 16px;
  background: rgba(0,0,0,0.7);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 200px;
`;

const AnimationButton = styled.button`
  background: rgba(255,255,255,0.2);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255,255,255,0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AnimationSelect = styled.select`
  background: rgba(0,0,0,0.5);
  color: white;
  border: 1px solid rgba(255,255,255,0.3);
  padding: 8px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;

  option {
    background: #1a1a1a;
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  z-index: 1000;
  opacity: ${props => props.$isVisible ? 1 : 0};
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255, 255, 255, 0.2);
  border-top: 4px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  color: white;
  font-size: 16px;
  font-weight: 600;
  margin-top: 20px;
  text-align: center;
`;

export default function ARGameScreen({ userName = '김철수', userRank = 3, initialScore = 2450, roomCode, isSoloMode = false, quizData = null, gameMode = 'qr-scan' }) {
  const navigate = useNavigate();
  const [isScanning, setIsScanning] = useState(false);
  const [arObjectVisible, setArObjectVisible] = useState(isSoloMode && gameMode === 'normal'); // 일반 모드에서는 처음부터 true
  const [rotation, setRotation] = useState({ x: 0, y: 0, z: 0 });
  const [autoRotate, setAutoRotate] = useState(false);
  const [rotationSpeed, setRotationSpeed] = useState(1);
  const [scale, setScale] = useState(1);
  const [userScore, setUserScore] = useState(initialScore);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const autoRotateRef = useRef(false);
  const rotationSpeedRef = useRef(1);
  const rotationRef = useRef({ x: 0, y: 0, z: 0 });
  const scaleRef = useRef(1);

  // 드래그 회전 관련 상태
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [lastRotation, setLastRotation] = useState({ x: 0, y: 0, z: 0 });
  
  // 퀴즈 관련 상태
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [isQuizComplete, setIsQuizComplete] = useState(false);

  // 시간 추적 관련 상태
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionTimeTaken, setQuestionTimeTaken] = useState([]); // 각 문제별 걸린 시간 (초)
  const [remainingTime, setRemainingTime] = useState(null); // 남은 시간 (초)
  const [isTimeUp, setIsTimeUp] = useState(false); // 시간 초과 여부

  // DB에서 가져온 퀴즈 데이터
  const [dbQuizData, setDbQuizData] = useState(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(true);
  const [dbArObjects, setDbArObjects] = useState([]); // DB에서 가져온 AR 오브젝트 목록

  // 오브젝트 관리 상태
  const [currentObjectIndex, setCurrentObjectIndex] = useState(0);
  const [isObjectTransitioning, setIsObjectTransitioning] = useState(false);

  // Solo 모드: 현재 오브젝트의 DB 저장 점수
  const [currentObjectScore, setCurrentObjectScore] = useState(0);
  
  // 각 오브젝트별 문제 완료 상태 추적
  const [objectQuizStatus, setObjectQuizStatus] = useState({});

  // QR 스캔 모드: 스캔된 오브젝트 순서 추적
  const [scannedObjects, setScannedObjects] = useState([]); // ar_object_id 배열
  const [showQRScanButton, setShowQRScanButton] = useState(false); // 퀴즈 완료 후 QR 스캔 버튼 표시

  // ants_answer 기반 총 오브젝트 개수
  const [totalExpectedObjects, setTotalExpectedObjects] = useState(null);

  // 최종 랭킹 화면 상태
  const [showFinalRanking, setShowFinalRanking] = useState(false);

  // Solo 모드: 명예의 전당 (랭킹)
  const [showHallOfFame, setShowHallOfFame] = useState(false);
  const [rankingData, setRankingData] = useState([]);
  const [myRanking, setMyRanking] = useState(null);

  // 애니메이션 관련 상태
  const [animations, setAnimations] = useState([]);
  const [currentAnimationIndex, setCurrentAnimationIndex] = useState(0);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(true);

  // Three.js scene 초기화 완료 상태
  const [isSceneReady, setIsSceneReady] = useState(false);

  // GLB 모델 로딩 상태
  const [isLoadingModel, setIsLoadingModel] = useState(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const threeCanvasRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const meshRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mixerRef = useRef(null);
  const clockRef = useRef(new THREE.Clock());
  const audioListenerRef = useRef(null);
  const audioSourcesRef = useRef([]);

  // DB에서 퀴즈 데이터 로드
  useEffect(() => {
    // quizData prop이 전달되었으면 prop 사용 (Solo 모드)
    if (quizData) {
      console.log('✅ quizData prop 사용:', quizData);

      // AR 오브젝트 목록 생성
      const objects = quizData.objects.map((obj, index) => {
        const objName = obj.ar_object_name || `객체 ${index + 1}`;
        return {
          id: index + 1,
          name: objName,
          image: obj.ar_object_image || '/ThumbnailEx1.jpg',
          type: 'db-object',
          description: `${objName} 관련 문제`,
          questionSet: objName,
          ar_object_id: obj.ar_object_id,
          questions: obj.questions // 문제 데이터 직접 저장
        };
      });

      setDbArObjects(objects);
      setIsLoadingQuiz(false);
      return;
    }

    // prop이 없으면 기존 로직 (멀티플레이 모드)
    const loadQuizData = async () => {
      try {
        const quizId = 54; // set_id
        const response = await fetch(`http://localhost:3000/auth/get-quiz-detail/${quizId}`);
        const data = await response.json();

        if (data.success) {
          console.log('✅ 퀴즈 데이터 로드 성공:', data.quiz);
          setDbQuizData(data.quiz);

          // AR 오브젝트 목록 추출 (문제들을 AR 오브젝트별로 그룹화)
          if (data.quiz.problems) {
            const objectMap = new Map();

            data.quiz.problems.forEach((problem) => {
              const arObject = problem.arObject || '알 수 없는 객체';
              const arObjectImage = problem.arObjectImage || '/ThumbnailEx1.jpg';

              if (!objectMap.has(arObject)) {
                objectMap.set(arObject, {
                  id: objectMap.size + 1,
                  name: arObject,
                  image: arObjectImage,
                  type: 'db-object',
                  description: `${arObject} 관련 문제`,
                  questionSet: arObject
                });
              }
            });

            const objects = Array.from(objectMap.values());
            console.log('📦 AR 오브젝트 목록:', objects);
            setDbArObjects(objects);
          }
        } else {
          console.error('❌ 퀴즈 데이터 로드 실패:', data.error);
        }
      } catch (error) {
        console.error('❌ 퀴즈 데이터 로드 에러:', error);
      } finally {
        setIsLoadingQuiz(false);
      }
    };

    loadQuizData();
  }, [quizData]);

  // 타이머 카운트다운
  useEffect(() => {
    if (!showInteractionModal || isQuizComplete || remainingTime === null) {
      return;
    }

    if (remainingTime <= 0) {
      setIsTimeUp(true);
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime(prev => {
        if (prev <= 1) {
          setIsTimeUp(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showInteractionModal, isQuizComplete, remainingTime]);

  // URL에서 GLB/GLTF 파일 로드 함수
  const loadGLTFFromURL = useCallback((url) => {
    const scene = sceneRef.current;
    if (!scene || !url) return;

    // 로딩 시작
    setIsLoadingModel(true);

    // 기존 메시 제거
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null;
    }

    // 기존 애니메이션 믹서 정리
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
      mixerRef.current = null;
    }

    const loader = new GLTFLoader();

    loader.load(
      url,
      (gltf) => {
        const model = gltf.scene;

        // 모델 크기 정규화
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        model.scale.setScalar(scale);

        // 중심 맞추기
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center.multiplyScalar(scale));

        scene.add(model);
        meshRef.current = model;

        // 애니메이션 처리
        if (gltf.animations && gltf.animations.length > 0) {
          setAnimations(gltf.animations);

          const mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;

          // 첫 번째 애니메이션 자동 재생
          const action = mixer.clipAction(gltf.animations[0]);
          action.play();
          setCurrentAnimationIndex(0);
          setIsAnimationPlaying(true);
        } else {
          setAnimations([]);
          setCurrentAnimationIndex(0);
          setIsAnimationPlaying(false);
        }

        setArObjectVisible(true);
        setIsLoadingModel(false);
      },
      undefined,
      (error) => {
        console.error('❌ GLB 모델 로드 실패:', error);
        setIsLoadingModel(false);
      }
    );
  }, []);

  // 현재 오브젝트의 GLB 모델 로드
  useEffect(() => {
    // scene이 준비될 때까지 대기
    if (!isSceneReady) {
      return;
    }

    if (quizData && quizData.objects && quizData.objects[currentObjectIndex]) {
      const currentObject = quizData.objects[currentObjectIndex];
      const modelUrl = currentObject.ar_object_model;

      if (modelUrl) {
        loadGLTFFromURL(modelUrl);
      } else {
        // 모델이 없으면 기존 CSS cube 표시
        setArObjectVisible(true);
      }
    }
  }, [currentObjectIndex, quizData, loadGLTFFromURL, isSceneReady]);

  // Solo 모드: 오브젝트 변경 시 해당 오브젝트의 점수 조회
  useEffect(() => {
    if (isSoloMode && quizData) {
      const fetchObjectScore = async () => {
        try {
          const response = await fetch(`http://localhost:3000/api/auth/solo-play/score/${userName}/${quizData.set_id}`, {
            credentials: 'include'
          });

          const result = await response.json();
          if (result.success && result.data) {
            const objScoreKey = `obj${currentObjectIndex + 1}score`;
            const score = result.data[objScoreKey] || 0;
            setCurrentObjectScore(score);
            console.log(`📊 오브젝트 ${currentObjectIndex + 1} 점수 조회: ${score}점`);
          } else {
            setCurrentObjectScore(0);
          }
        } catch (error) {
          console.error('💥 점수 조회 오류:', error);
          setCurrentObjectScore(0);
        }
      };

      fetchObjectScore();
    }
  }, [currentObjectIndex, isSoloMode, quizData, userName]);

  // ref 동기화
  useEffect(() => {
    autoRotateRef.current = autoRotate;
  }, [autoRotate]);

  useEffect(() => {
    rotationSpeedRef.current = rotationSpeed;
  }, [rotationSpeed]);

  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  // 마우스 휠 줌
useEffect(() => {
  const handleWheel = (e) => {
    if (!arObjectVisible) return;
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? -0.2 : 0.2;
    setScale(prev => Math.max(0.1, Math.min(10, prev + delta)));
  };

  const element = document.getElementById('game-container');
  if (element) {
    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => element.removeEventListener('wheel', handleWheel);
  }
}, [arObjectVisible]);

// 더블클릭 리셋
useEffect(() => {
  const handleDoubleClick = () => {
    if (!arObjectVisible) return;
    setRotation({ x: 0, y: 0, z: 0 });
    setScale(1);
  };

  window.addEventListener('dblclick', handleDoubleClick);
  return () => window.removeEventListener('dblclick', handleDoubleClick);
}, [arObjectVisible]);

    // Three.js 초기화
  useEffect(() => {
    if (!threeCanvasRef.current) return;

    // 씬 생성
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // 카메라 생성
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;
    cameraRef.current = camera;

    // 렌더러 생성
    const renderer = new THREE.WebGLRenderer({
      canvas: threeCanvasRef.current,
      alpha: true,
      antialias: true,
      preserveDrawingBuffer: true // 캡처를 위해 필수
    });

    // 반응형 크기 설정
    const canvasWidth = Math.min(window.innerWidth * 0.9, 1200);
    const canvasHeight = Math.min(window.innerHeight * 0.9, 1200);
    renderer.setSize(canvasWidth, canvasHeight);
    renderer.setClearColor(0x000000, 0);
    rendererRef.current = renderer;

    // 윈도우 리사이즈 핸들러
    const handleResize = () => {
      const newWidth = Math.min(window.innerWidth * 0.9, 1200);
      const newHeight = Math.min(window.innerHeight * 0.9, 1200);
      renderer.setSize(newWidth, newHeight);
      camera.aspect = newWidth / newHeight;
      camera.updateProjectionMatrix();
    };
    window.addEventListener('resize', handleResize);

    // 오디오 리스너 추가
    const audioListener = new THREE.AudioListener();
    camera.add(audioListener);
    audioListenerRef.current = audioListener;

    // 조명 추가
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    directionalLight.position.set(5, 5, 5);
    scene.add(directionalLight);

    // 애니메이션 루프
    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      // 애니메이션 믹서 업데이트
      if (mixerRef.current) {
        const delta = clockRef.current.getDelta();
        mixerRef.current.update(delta);
      }

      if (meshRef.current) {
        // ref에서 최신 값을 읽어옴
        meshRef.current.rotation.x = THREE.MathUtils.degToRad(rotationRef.current.x);
        meshRef.current.rotation.y = THREE.MathUtils.degToRad(rotationRef.current.y);
        meshRef.current.rotation.z = THREE.MathUtils.degToRad(rotationRef.current.z);
        meshRef.current.scale.setScalar(scaleRef.current);
      }

      renderer.render(scene, camera);
    };
    animate();

    // scene 초기화 완료 플래그 설정
    setIsSceneReady(true);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      // 오디오 정리
      audioSourcesRef.current.forEach(audio => {
        if (audio.isPlaying) audio.stop();
      });
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, []);

  // OBJ 파일 로드 함수
  const loadOBJFile = (file) => {
    const scene = sceneRef.current;
    if (!scene) return;

    // 기존 메시 제거
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const contents = e.target.result;
      const loader = new OBJLoader();

      try {
        const object = loader.parse(contents);
        
        // 모델 크기 정규화
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        object.scale.setScalar(scale);
        
        // 중심 맞추기
        const center = box.getCenter(new THREE.Vector3());
        object.position.sub(center.multiplyScalar(scale));

        // 기본 재질 적용
        object.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            child.material = new THREE.MeshStandardMaterial({
              color: 0x00ff00,
              metalness: 0.5,
              roughness: 0.5
            });
          }
        });

        scene.add(object);
        meshRef.current = object;
        setArObjectVisible(true);
        
        console.log('OBJ 파일 로드 완료!');
      } catch (error) {
        console.error('OBJ 로드 실패:', error);
        alert('OBJ 파일을 로드할 수 없습니다.');
      }
    };

    reader.readAsText(file);
  };

  // MTL + OBJ 함께 로드 (텍스처가 있는 경우)
  const loadOBJWithMTL = (objFile, mtlFile) => {
    const scene = sceneRef.current;
    if (!scene) return;

    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null;
    }

    const mtlLoader = new MTLLoader();
    const objLoader = new OBJLoader();

    const mtlReader = new FileReader();
    mtlReader.onload = (e) => {
      const materials = mtlLoader.parse(e.target.result);
      materials.preload();
      objLoader.setMaterials(materials);

      const objReader = new FileReader();
      objReader.onload = (e) => {
        try {
          const object = objLoader.parse(e.target.result);
          
          const box = new THREE.Box3().setFromObject(object);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          object.scale.setScalar(scale);
          
          const center = box.getCenter(new THREE.Vector3());
          object.position.sub(center.multiplyScalar(scale));

          scene.add(object);
          meshRef.current = object;
          setArObjectVisible(true);
          
          console.log('OBJ + MTL 파일 로드 완료!');
        } catch (error) {
          console.error('OBJ 로드 실패:', error);
          alert('OBJ 파일을 로드할 수 없습니다.');
        }
      };
      objReader.readAsText(objFile);
    };
    mtlReader.readAsText(mtlFile);
  };

  const handleFileUpload = (e) => {
  const files = Array.from(e.target.files);
  if (files.length === 0) return;

  setUploadedFiles(files.map(f => f.name));

  // GLB/GLTF 파일 찾기
  const gltfFile = files.find(f =>
    f.name.toLowerCase().endsWith('.glb') ||
    f.name.toLowerCase().endsWith('.gltf')
  );

  if (gltfFile) {
    loadGLTFFile(gltfFile);
    return;
  }

  // FBX 파일 찾기
  const fbxFile = files.find(f => f.name.toLowerCase().endsWith('.fbx'));

  if (fbxFile) {
    loadFBXFile(fbxFile);
    return;
  }

  // OBJ 파일 찾기
  const objFile = files.find(f => f.name.toLowerCase().endsWith('.obj'));
  if (!objFile) {
    alert('GLB, GLTF, FBX 또는 OBJ 파일을 선택해주세요.');
    return;
  }

  // MTL 파일 찾기
  const mtlFile = files.find(f => f.name.toLowerCase().endsWith('.mtl'));

  // 텍스처 이미지 파일들
  const imageFiles = files.filter(f =>
    f.name.toLowerCase().match(/\.(jpg|jpeg|png|bmp|gif)$/)
  );

  console.log('업로드된 파일:', {
    obj: objFile.name,
    mtl: mtlFile?.name,
    images: imageFiles.map(f => f.name)
  });

  if (mtlFile && imageFiles.length > 0) {
    loadOBJWithMTLAndTextures(objFile, mtlFile, imageFiles);
  } else if (mtlFile) {
    loadOBJWithMTL(objFile, mtlFile);
  } else {
    loadOBJFile(objFile);
  }
};

// MTL과 텍스처 이미지를 함께 로드하는 새로운 함수
const loadOBJWithMTLAndTextures = (objFile, mtlFile, imageFiles) => {
  const scene = sceneRef.current;
  if (!scene) return;

  if (meshRef.current) {
    scene.remove(meshRef.current);
    meshRef.current = null;
  }

  // 이미지 파일들을 URL로 변환
  const imageUrls = {};
  imageFiles.forEach(file => {
    imageUrls[file.name] = URL.createObjectURL(file);
  });

  const mtlReader = new FileReader();
  mtlReader.onload = (e) => {
    const mtlContent = e.target.result;
    
    // MTL 파일 수정: 텍스처 경로를 blob URL로 대체
    let modifiedMtlContent = mtlContent;
    Object.keys(imageUrls).forEach(fileName => {
      const regex = new RegExp(fileName, 'g');
      modifiedMtlContent = modifiedMtlContent.replace(regex, imageUrls[fileName]);
    });

    const mtlLoader = new MTLLoader();
    const materials = mtlLoader.parse(modifiedMtlContent, '');
    materials.preload();

    const objLoader = new OBJLoader();
    objLoader.setMaterials(materials);

    const objReader = new FileReader();
    objReader.onload = (e) => {
      try {
        const object = objLoader.parse(e.target.result);
        
        // 모델 크기 정규화
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        object.scale.setScalar(scale);
        
        // 중심 맞추기
        const center = box.getCenter(new THREE.Vector3());
        object.position.sub(center.multiplyScalar(scale));

        scene.add(object);
        meshRef.current = object;
        setArObjectVisible(true);
        
        console.log('텍스처가 포함된 OBJ 모델 로드 완료!');
      } catch (error) {
        console.error('OBJ 로드 실패:', error);
        alert('OBJ 파일을 로드할 수 없습니다.');
      }
    };
    objReader.readAsText(objFile);
  };
  mtlReader.readAsText(mtlFile);
  };

  // GLB/GLTF 파일 로드 함수
  const loadGLTFFile = (file) => {
    const scene = sceneRef.current;
    if (!scene) return;

    // 기존 메시 제거
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null;
    }

    // 기존 애니메이션 믹서 정리
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
      mixerRef.current = null;
    }

    const loader = new GLTFLoader();
    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target.result;

      try {
        loader.parse(arrayBuffer, '', (gltf) => {
          const model = gltf.scene;

          // 모델 크기 정규화
          const box = new THREE.Box3().setFromObject(model);
          const size = box.getSize(new THREE.Vector3());
          const maxDim = Math.max(size.x, size.y, size.z);
          const scale = 2 / maxDim;
          model.scale.setScalar(scale);

          // 중심 맞추기
          const center = box.getCenter(new THREE.Vector3());
          model.position.sub(center.multiplyScalar(scale));

          scene.add(model);
          meshRef.current = model;
          setArObjectVisible(true);

          // 오디오 처리
          if (audioListenerRef.current) {
            // 기존 오디오 정리
            audioSourcesRef.current.forEach(audio => {
              if (audio.isPlaying) audio.stop();
            });
            audioSourcesRef.current = [];

            // 모델에서 AudioSource 찾기
            model.traverse((child) => {
              if (child.userData && child.userData.gltfExtensions) {
                const extensions = child.userData.gltfExtensions;
                if (extensions['KHR_audio']) {
                  // GLTF 오디오 익스텐션 처리
                  console.log('오디오 발견:', child);
                }
              }
            });

            // GLB 파일에 오디오 버퍼가 있는 경우
            if (gltf.parser && gltf.parser.json && gltf.parser.json.extensions) {
              const audioExt = gltf.parser.json.extensions['KHR_audio'];
              if (audioExt && audioExt.sources) {
                console.log('GLB 파일에 오디오 발견:', audioExt.sources.length + '개');

                // 각 오디오 소스 로드 및 재생
                audioExt.sources.forEach((audioSource, index) => {
                  gltf.parser.getDependency('buffer', audioSource.bufferView).then(buffer => {
                    const audioLoader = new THREE.AudioLoader();
                    const audio = new THREE.Audio(audioListenerRef.current);

                    // 버퍼를 AudioBuffer로 변환
                    audioListenerRef.current.context.decodeAudioData(
                      buffer.slice(0),
                      (audioBuffer) => {
                        audio.setBuffer(audioBuffer);
                        audio.setLoop(true);
                        audio.setVolume(0.5);
                        audio.play();
                        audioSourcesRef.current.push(audio);
                        console.log(`오디오 ${index + 1} 재생 시작`);
                      },
                      (error) => {
                        console.error('오디오 디코딩 실패:', error);
                      }
                    );
                  });
                });
              }
            }
          }

          // 애니메이션 처리
          if (gltf.animations && gltf.animations.length > 0) {
            const mixer = new THREE.AnimationMixer(model);
            mixerRef.current = mixer;

            // 애니메이션 리스트 저장
            setAnimations(gltf.animations);

            // 첫 번째 애니메이션 자동 재생
            const action = mixer.clipAction(gltf.animations[0]);
            action.setLoop(THREE.LoopRepeat); // 반복 재생
            action.clampWhenFinished = false;
            action.play();

            console.log(`✅ GLB 파일 로드 완료!`);
            console.log(`📋 ${gltf.animations.length}개의 애니메이션 발견:`);
            gltf.animations.forEach((anim, idx) => {
              console.log(`  ${idx + 1}. ${anim.name || '이름 없음'} (${anim.duration.toFixed(2)}초)`);
            });
            console.log(`▶️ 첫 번째 애니메이션 자동 재생 시작`);
          } else {
            console.log('⚠️ GLB 파일 로드 완료! (애니메이션 없음)');
            console.log('Unity에서 export 시 "Bake Animation" 옵션을 확인해주세요.');
            setAnimations([]);
          }
        }, (error) => {
          console.error('GLB 파싱 실패:', error);
          alert('GLB 파일을 로드할 수 없습니다.');
        });
      } catch (error) {
        console.error('GLB 로드 실패:', error);
        alert('GLB 파일을 로드할 수 없습니다.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // 애니메이션 재생/정지
  const toggleAnimation = () => {
    if (!mixerRef.current) return;

    if (isAnimationPlaying) {
      mixerRef.current.timeScale = 0; // 일시정지
    } else {
      mixerRef.current.timeScale = 1; // 재생
    }
    setIsAnimationPlaying(!isAnimationPlaying);
  };

  // 애니메이션 변경
  const changeAnimation = (index) => {
    if (!mixerRef.current || !animations[index]) return;

    // 모든 애니메이션 정지
    mixerRef.current.stopAllAction();

    // 선택한 애니메이션 재생
    const action = mixerRef.current.clipAction(animations[index]);
    action.reset();
    action.setLoop(THREE.LoopRepeat); // 반복 재생
    action.clampWhenFinished = false;
    action.play();

    // timeScale 복원 (일시정지 상태였을 수 있음)
    mixerRef.current.timeScale = 1;

    setCurrentAnimationIndex(index);
    setIsAnimationPlaying(true);

    const animName = animations[index].name || `애니메이션 ${index + 1}`;
    console.log(`🔄 애니메이션 변경: ${animName} (${animations[index].duration.toFixed(2)}초)`);
  };

  // FBX 파일 로드 함수
  const loadFBXFile = (file) => {
    const scene = sceneRef.current;
    if (!scene) return;

    // 기존 메시 제거
    if (meshRef.current) {
      scene.remove(meshRef.current);
      meshRef.current = null;
    }

    // 기존 애니메이션 믹서 정리
    if (mixerRef.current) {
      mixerRef.current.stopAllAction();
      mixerRef.current = null;
    }

    const loader = new FBXLoader();
    const reader = new FileReader();

    reader.onload = (e) => {
      const arrayBuffer = e.target.result;

      try {
        const model = loader.parse(arrayBuffer, '');

        // 모델 크기 정규화
        const box = new THREE.Box3().setFromObject(model);
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        model.scale.setScalar(scale);

        // 중심 맞추기
        const center = box.getCenter(new THREE.Vector3());
        model.position.sub(center.multiplyScalar(scale));

        scene.add(model);
        meshRef.current = model;
        setArObjectVisible(true);

        // 애니메이션 처리
        if (model.animations && model.animations.length > 0) {
          const mixer = new THREE.AnimationMixer(model);
          mixerRef.current = mixer;

          // 애니메이션 리스트 저장
          setAnimations(model.animations);

          // 첫 번째 애니메이션 자동 재생
          const action = mixer.clipAction(model.animations[0]);
          action.setLoop(THREE.LoopRepeat);
          action.clampWhenFinished = false;
          action.play();

          console.log(`✅ FBX 파일 로드 완료!`);
          console.log(`📋 ${model.animations.length}개의 애니메이션 발견:`);
          model.animations.forEach((anim, idx) => {
            console.log(`  ${idx + 1}. ${anim.name || '이름 없음'} (${anim.duration.toFixed(2)}초)`);
          });
          console.log(`▶️ 첫 번째 애니메이션 자동 재생 시작`);
        } else {
          console.log('⚠️ FBX 파일 로드 완료! (애니메이션 없음)');
          console.log('Unity에서 export 시 애니메이션이 포함되었는지 확인해주세요.');
          setAnimations([]);
        }
      } catch (error) {
        console.error('FBX 로드 실패:', error);
        alert('FBX 파일을 로드할 수 없습니다.');
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // AR 오브젝트 데이터 (DB 데이터가 있으면 사용, 없으면 하드코딩 데이터)
  const defaultArObjects = [
    {
      id: 1,
      name: "기하학적 큐브",
      type: "cube",
      description: "기본 3D 큐브 오브젝트",
      questionSet: "cube"
    },
    {
      id: 2,
      name: "역사 유물",
      type: "artifact",
      description: "고대 유적 복원 모델",
      questionSet: "history"
    },
    {
      id: 3,
      name: "생물학 구조",
      type: "biological",
      description: "세포 구조 3D 모델",
      questionSet: "biology"
    },
    {
      id: 4,
      name: "화학 분자",
      type: "molecule",
      description: "분자 구조 시각화",
      questionSet: "chemistry"
    },
    {
      id: 5,
      name: "지리 지형",
      type: "geography",
      description: "지형 고도 모델",
      questionSet: "geography"
    }
  ];

  // DB에서 가져온 AR 오브젝트가 있으면 사용, 없으면 기본 데이터 사용
  const arObjects = dbArObjects.length > 0 ? dbArObjects : defaultArObjects;

  // 오브젝트별 퀴즈 문제 데이터
  const questionSets = {
    cube: [
      {
        question: "AR 오브젝트에서 빨간색 면에 표시된 방향은?",
        options: ["위쪽 화살표", "아래쪽 화살표", "왼쪽 화살표", "오른쪽 화살표"],
        correct: 0,
        points: 100
      },
      {
        question: "이 3D 큐브는 총 몇 개의 면을 가지고 있나요?",
        options: ["4개", "5개", "6개", "8개"],
        correct: 2,
        points: 150
      },
      {
        question: "GREEN 텍스트가 표시된 면의 화살표 방향은?",
        options: ["위쪽", "아래쪽", "왼쪽", "오른쪽"],
        correct: 2,
        points: 200
      },
      {
        question: "AR 오브젝트의 상단(TOP) 면의 색상은?",
        options: ["빨간색", "파란색", "보라색", "노란색"],
        correct: 2,
        points: 250
      },
      {
        question: "이 AR 시스템에서 오브젝트를 회전시킬 수 있는 축의 개수는?",
        options: ["1개", "2개", "3개", "4개"],
        correct: 2,
        points: 300
      }
    ],
    history: [
      {
        question: "이 역사 유물은 어느 시대의 것으로 추정되나요?",
        options: ["고대", "중세", "근세", "현대"],
        correct: 0,
        points: 100
      },
      {
        question: "유물의 재료로 추정되는 것은?",
        options: ["청동", "철", "석재", "도자기"],
        correct: 2,
        points: 150
      },
      {
        question: "이런 형태의 유물의 주된 용도는?",
        options: ["장식용", "실용품", "제례용", "무기"],
        correct: 2,
        points: 200
      }
    ],
    biology: [
      {
        question: "이 세포 구조에서 가장 큰 기관은?",
        options: ["핵", "미토콘드리아", "리보솜", "골지체"],
        correct: 0,
        points: 100
      },
      {
        question: "세포막의 주요 구성 성분은?",
        options: ["단백질", "지질", "탄수화물", "핵산"],
        correct: 1,
        points: 150
      }
    ],
    chemistry: [
      {
        question: "이 분자의 화학 결합 유형은?",
        options: ["이온 결합", "공유 결합", "금속 결합", "수소 결합"],
        correct: 1,
        points: 100
      },
      {
        question: "분자의 기하학적 구조는?",
        options: ["선형", "각형", "삼각형", "사면체"],
        correct: 3,
        points: 150
      }
    ],
    geography: [
      {
        question: "이 지형의 최고 높이는 대략?",
        options: ["100m", "500m", "1000m", "2000m"],
        correct: 2,
        points: 100
      },
      {
        question: "이런 지형이 형성되는 주요 원인은?",
        options: ["풍화", "침식", "화산 활동", "지각 운동"],
        correct: 3,
        points: 150
      }
    ]
  };

  // 현재 오브젝트의 문제 세트 가져오기
  const getCurrentQuestions = () => {
    const currentObject = arObjects[currentObjectIndex];

    // quizData prop으로 전달된 데이터 사용 (Solo 모드)
    if (quizData && currentObject?.questions) {
      return currentObject.questions.map(q => {
        // correct_answer가 배열이면 첫 번째 값, 아니면 그대로 사용
        let correctIndex = q.correct_answer;
        if (Array.isArray(correctIndex)) {
          correctIndex = correctIndex.length > 0 ? correctIndex[0] : 0;
        }

        // 문자열이면 숫자로 변환 (타입 일치 보장)
        if (typeof correctIndex === 'string') {
          correctIndex = parseInt(correctIndex, 10);
        }

        // 유효하지 않은 숫자면 0으로 설정
        if (isNaN(correctIndex)) {
          correctIndex = 0;
        }

        // 디버그 로그 제거 (무한 루프 방지)

        return {
          question: q.question_text,
          options: q.choices,
          correct: correctIndex, // 0-based index (숫자)
          points: 0, // 초기 점수
          timeLimit: q.question_timelimit || 30 // 제한 시간 (초)
        };
      });
    }

    // DB 데이터가 로드되었으면 DB 데이터 사용
    if (dbQuizData && dbQuizData.problems) {
      // 현재 AR 오브젝트에 해당하는 문제만 필터링
      const filteredProblems = dbQuizData.problems.filter(
        problem => (problem.arObject || '알 수 없는 객체') === currentObject?.name
      );

      return filteredProblems.map((problem, index) => ({
        question: problem.question_text || problem.question,
        options: problem.choices || [],
        correct: problem.correct_answer && problem.correct_answer.length > 0
          ? problem.correct_answer[0]
          : 0,
        points: 100, // 기본 점수
        timeLimit: parseInt(problem.timelimit) || 30 // 제한 시간 (초)
      }));
    }

    // DB 데이터가 없으면 기존 하드코딩된 데이터 사용
    return questionSets[currentObject?.questionSet] || [];
  };

  // 모든 문제를 완료했는지 확인
  const isAllQuestionsCompleted = () => {
    return arObjects.every(obj => objectQuizStatus[obj.id] === true);
  };

  // 최종 랭킹 데이터 생성 (가상의 다른 플레이어들과 함께)
  const getFinalRankingData = () => {
    const playerEmojis = ['🎯', '🏆', '👑', '🎮', '⭐'];
    const playerColors = [
      'linear-gradient(135deg, #4ECDC4, #44A08D)',
      'linear-gradient(135deg, #FFD700, #FFA500)',
      'linear-gradient(135deg, #FF6B6B, #FF8E53)',
      'linear-gradient(135deg, #667eea, #764ba2)',
      'linear-gradient(135deg, #f093fb, #f5576c)'
    ];

    // 가상의 다른 플레이어들
    const otherPlayers = [
      { name: '김민수', score: Math.floor(Math.random() * 2000) + 3000 },
      { name: '이지은', score: Math.floor(Math.random() * 1500) + 2500 },
      { name: '박준호', score: Math.floor(Math.random() * 1000) + 2000 },
      { name: '정소영', score: Math.floor(Math.random() * 500) + 1500 }
    ];

    // 현재 사용자 포함
    const allPlayers = [
      {
        id: 'current-user',
        name: userName,
        score: userScore,
        isCurrentUser: true
      },
      ...otherPlayers.map((player, index) => ({
        id: `player-${index}`,
        name: player.name,
        score: player.score,
        isCurrentUser: false
      }))
    ];

    // 점수 순으로 정렬하고 아바타와 색상 추가
    return allPlayers
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((player, index) => ({
        ...player,
        avatar: playerEmojis[index],
        bgColor: playerColors[index]
      }));
  };

  useEffect(() => {
    startCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.log('카메라 접근 실패:', error);
    }
  };

  const scanIntervalRef = useRef(null);

  const handleQRScan = () => {
    setIsScanning(true);

    // QR 코드 스캔 시작
    const scanQRCode = () => {
      if (!videoRef.current || !canvasRef.current) {
        return;
      }

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');

      if (video.readyState === video.HAVE_ENOUGH_DATA) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(imageData.data, imageData.width, imageData.height);

        if (code) {
          console.log('✅ QR 코드 인식:', code.data);

          try {
            const qrData = JSON.parse(code.data);

            // QR 코드에서 AR 객체 정보 추출
            if (qrData.ar_object_id && quizData) {
              const objectIndex = quizData.objects.findIndex(
                obj => obj.ar_object_id === qrData.ar_object_id
              );

              if (objectIndex !== -1) {
                console.log('✅ AR 객체 찾음:', qrData.ar_object_name, 'Index:', objectIndex);

                // QR 스캔 모드: 스캔된 오브젝트 추적
                if (gameMode === 'qr-scan' && !scannedObjects.includes(qrData.ar_object_id)) {
                  setScannedObjects(prev => [...prev, qrData.ar_object_id]);
                  console.log(`📦 새 오브젝트 스캔됨: ${qrData.ar_object_name} (${scannedObjects.length + 1}번째)`);
                }

                setCurrentObjectIndex(objectIndex);
                setIsScanning(false);
                setArObjectVisible(true);
                setShowQRScanButton(false); // QR 스캔 버튼 숨기기
                if (scanIntervalRef.current) {
                  cancelAnimationFrame(scanIntervalRef.current);
                  scanIntervalRef.current = null;
                }
                return;
              }
            }
          } catch (error) {
            console.error('❌ QR 코드 파싱 실패:', error);
          }
        }
      }

      // QR 코드를 찾지 못하면 계속 스캔
      scanIntervalRef.current = requestAnimationFrame(scanQRCode);
    };

    scanQRCode();
  };

  // 컴포넌트 언마운트 시 스캔 중지
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) {
        cancelAnimationFrame(scanIntervalRef.current);
      }
    };
  }, []);

  const rotateObject = (axis, direction) => {
    setRotation(prev => {
      const newRotation = {
        ...prev,
        [axis]: prev[axis] + (direction * 30)
      };
      
      newRotation.x = ((newRotation.x % 360) + 360) % 360;
      newRotation.y = ((newRotation.y % 360) + 360) % 360;
      newRotation.z = ((newRotation.z % 360) + 360) % 360;
      
      return newRotation;
    });
  };

  const scaleObject = (direction) => {
    setScale(prev => Math.max(0.1, Math.min(10, prev + (direction * 0.3))));
  };

  const handlePointerDown = (e) => {
    if (!arObjectVisible) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setLastRotation(rotation);
    e.preventDefault();
  };

  const handlePointerMove = (e) => {
    if (!isDragging || !arObjectVisible) return;

    const deltaX = e.clientX - dragStart.x;
    const deltaY = e.clientY - dragStart.y;
    
    const sensitivity = 0.5;
    const newRotationY = lastRotation.y + deltaX * sensitivity;
    const newRotationX = lastRotation.x - deltaY * sensitivity;
    
    setRotation({
      x: ((newRotationX % 360) + 360) % 360,
      y: ((newRotationY % 360) + 360) % 360,
      z: rotation.z
    });
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  const captureScreen = async () => {
    const gameContainer = document.getElementById('game-container');
    if (!gameContainer) return;

    try {
      const containerRect = gameContainer.getBoundingClientRect();

      // html2canvas 라이브러리 동적 로드
      if (!window.html2canvas) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        document.head.appendChild(script);
        await new Promise((resolve) => { script.onload = resolve; });
      }

      // 1. HTML/CSS 요소 캡처 (배경, 비디오, UI 등)
      const htmlCanvas = await window.html2canvas(gameContainer, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#000000',
        scale: 2,
        logging: false
      });

      // 최종 합성 캔버스 생성
      const finalCanvas = document.createElement('canvas');
      finalCanvas.width = htmlCanvas.width;
      finalCanvas.height = htmlCanvas.height;
      const finalCtx = finalCanvas.getContext('2d');

      // HTML 캔버스를 최종 캔버스에 그리기
      finalCtx.drawImage(htmlCanvas, 0, 0);

      // 2. Three.js 캔버스가 있으면 함께 합성
      if (threeCanvasRef.current && meshRef.current && arObjectVisible) {
        const threeCanvas = threeCanvasRef.current;
        const threeRect = threeCanvas.getBoundingClientRect();

        // Three.js 캔버스의 상대적 위치 계산 (scale 2 고려)
        const offsetX = (threeRect.left - containerRect.left) * 2;
        const offsetY = (threeRect.top - containerRect.top) * 2;
        const width = threeRect.width * 2;
        const height = threeRect.height * 2;

        // Three.js 캔버스를 최종 캔버스에 오버레이
        finalCtx.drawImage(threeCanvas, offsetX, offsetY, width, height);

        console.log('Three.js 캔버스 합성 완료:', { offsetX, offsetY, width, height });
      }

      // 다운로드
      finalCanvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `ar-capture-${Date.now()}.png`;
          a.click();
          URL.revokeObjectURL(url);
          console.log('캡처 완료!');
        }
      });
    } catch (error) {
      console.error('캡처 실패:', error);
      alert('화면 캡처에 실패했습니다.');
    }
  };

  // 현재 오브젝트의 퀴즈 완료 상태 확인
  const isCurrentObjectQuizCompleted = () => {
    const currentObject = arObjects[currentObjectIndex];
    return objectQuizStatus[currentObject?.id] === true;
  };

  // 퀴즈 관련 함수들
  const handleStartQuiz = () => {
    // 이미 완료된 퀴즈는 시작하지 않음
    if (isCurrentObjectQuizCompleted()) {
      return;
    }

    const currentQuestions = getCurrentQuestions();
    if (currentQuestions.length === 0) {
      console.log('해당 오브젝트에 대한 문제가 없습니다.');
      return;
    }

    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setQuestionTimeTaken([]);
    setIsQuizComplete(false);
    setShowInteractionModal(true);
    setQuestionStartTime(Date.now()); // 첫 문제 시작 시간 기록
    setIsTimeUp(false); // 시간 초과 상태 초기화

    // 제한 시간 설정
    const timeLimit = currentQuestions[0]?.timeLimit || 30;
    setRemainingTime(timeLimit);
  };

  // 랭킹 조회 함수
  const fetchRanking = async () => {
    try {
      console.log('🔍 랭킹 조회 시작 - set_id:', quizData.set_id);
      const response = await fetch(`/auth/solo-play/ranking/${quizData.set_id}?limit=100`, {
        credentials: 'include'
      });

      const result = await response.json();
      if (result.success) {
        console.log('✅ 랭킹 조회 성공:', result.data);
        setRankingData(result.data);

        // 내 랭킹 찾기
        const myRank = result.data.findIndex(item => item.user_id === userName) + 1;
        const myData = result.data.find(item => item.user_id === userName);
        if (myData) {
          setMyRanking({ rank: myRank, ...myData });
        }

        console.log('🏆 명예의 전당 표시');
        setShowHallOfFame(true);
      } else {
        console.error('❌ 랭킹 조회 실패:', result.error);
        // 실패해도 명예의 전당은 표시
        console.log('🏆 명예의 전당 표시 (빈 랭킹)');
        setShowHallOfFame(true);
      }
    } catch (error) {
      console.error('💥 랭킹 조회 API 호출 오류:', error);
      // API 오류가 있어도 명예의 전당은 표시
      console.log('🏆 명예의 전당 표시 (API 오류)');
      setShowHallOfFame(true);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    const newAnswers = [...selectedAnswers];
    newAnswers[currentQuestion] = answerIndex;
    setSelectedAnswers(newAnswers);

    // 답변 선택 시 걸린 시간 기록 (초 단위)
    if (questionStartTime) {
      const timeTaken = (Date.now() - questionStartTime) / 1000;
      const newTimeTaken = [...questionTimeTaken];
      newTimeTaken[currentQuestion] = timeTaken;
      setQuestionTimeTaken(newTimeTaken);
    }
  };

  const handleNextQuestion = () => {
    const currentQuestions = getCurrentQuestions();
    if (currentQuestion < currentQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setQuestionStartTime(Date.now()); // 다음 문제 시작 시간 기록

      // 다음 문제의 제한 시간 설정
      const nextTimeLimit = currentQuestions[currentQuestion + 1]?.timeLimit || 30;
      setRemainingTime(nextTimeLimit);
      setIsTimeUp(false); // 시간 초과 상태 초기화
    }
  };

  const calculateScore = () => {
    const currentQuestions = getCurrentQuestions();
    let totalScore = 0;

    currentQuestions.forEach((question, index) => {
      const userAnswer = selectedAnswers[index];
      const correctAnswer = question.correct;
      const isCorrect = userAnswer === correctAnswer;

      console.log(`문제 ${index + 1}: 선택=${userAnswer} (타입: ${typeof userAnswer}), 정답=${correctAnswer} (타입: ${typeof correctAnswer}), 일치=${isCorrect}`);

      if (isCorrect) {
        // 기본 점수 (정답 맞춤) - 50점
        const baseScore = 50;
        totalScore += baseScore;

        // 시간 점수 계산: (남은시간/제한시간) * 100점
        const timeLimit = question.timeLimit || 30; // 제한 시간 (초), 기본값 30초
        const timeTaken = questionTimeTaken[index] || 0; // 걸린 시간 (초)
        const remainingTime = Math.max(0, timeLimit - timeTaken); // 남은 시간 (음수 방지)
        const timeScore = Math.floor((remainingTime / timeLimit) * 100); // 시간 점수 (최대 100점)

        totalScore += timeScore;

        console.log(`문제 ${index + 1}: 기본점수=${baseScore}, 제한시간=${timeLimit}초, 걸린시간=${timeTaken.toFixed(2)}초, 남은시간=${remainingTime.toFixed(2)}초, 시간점수=${timeScore}, 총=${baseScore + timeScore}점`);
      }
    });

    return totalScore;
  };

  const handleQuizSubmit = async () => {
    const currentQuestions = getCurrentQuestions();
    const earnedPoints = calculateScore();
    setUserScore(prev => prev + earnedPoints);
    setIsQuizComplete(true);

    // 현재 오브젝트의 퀴즈를 완료 상태로 마킹
    const currentObject = arObjects[currentObjectIndex];
    const newQuizStatus = {
      ...objectQuizStatus,
      [currentObject.id]: true
    };
    setObjectQuizStatus(newQuizStatus);

    const correctCount = selectedAnswers.filter((answer, index) =>
      answer === currentQuestions[index].correct
    ).length;

    console.log(`퀴즈 완료! ${correctCount}/${currentQuestions.length}문제 정답! +${earnedPoints}점 획득!`);

    // Solo 모드일 때 점수 DB 저장
    if (isSoloMode && quizData) {
      try {
        console.log('💾 점수 저장 시작:', { userName, set_id: quizData.set_id, object_index: currentObjectIndex, score: earnedPoints });
        const response = await fetch('/auth/solo-play/save-score', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            user_id: userName,
            set_id: quizData.set_id,
            object_index: currentObjectIndex,
            score: earnedPoints,
            game_mode: gameMode // 'qr-scan' 또는 일반 모드
          })
        });

        const result = await response.json();
        if (result.success) {
          console.log('✅ 점수 저장 성공:', result.data);

          // 저장된 점수 중 현재 오브젝트의 점수를 state에 저장
          const objScoreKey = `obj${currentObjectIndex + 1}score`;
          const savedScore = result.data[objScoreKey] || 0;
          setCurrentObjectScore(savedScore);
          console.log(`📊 현재 오브젝트(${currentObjectIndex + 1}) 점수: ${savedScore}점`);
        } else {
          console.error('❌ 점수 저장 실패:', result.error);
        }
      } catch (error) {
        console.error('💥 점수 저장 API 호출 오류:', error);
      }
    }

    // 완료된 오브젝트 개수 계산
    const completedCount = Object.values(newQuizStatus).filter(status => status === true).length;
    console.log(`📊 완료된 오브젝트: ${completedCount}개 / ${arObjects.length}개`);
    console.log('📊 현재 퀴즈 상태:', newQuizStatus);
    console.log('📊 isSoloMode:', isSoloMode, 'quizData:', !!quizData);

    // Solo 모드: ants_answer 기반으로 완료 체크
    if (isSoloMode && quizData) {
      // 총 예상 오브젝트 개수와 비교
      const expectedCount = arObjects.length; // quizData.objects.length와 동일
      console.log(`📊 완료 체크: completedCount=${completedCount}, expectedCount=${expectedCount}`);

      if (completedCount >= expectedCount) {
        console.log('🎉 모든 문제를 완료했습니다! 명예의 전당으로 이동합니다...');

        // 명예의 전당 표시 (1초 후)
        setTimeout(() => {
          console.log('⏰ setTimeout 실행: fetchRanking 호출');
          fetchRanking().catch(err => {
            console.error('💥 랭킹 조회 실패, 빈 명예의 전당 표시:', err);
            // 실패해도 명예의 전당은 표시
            setShowHallOfFame(true);
          });
        }, 1000);
      } else {
        console.log(`📊 아직 완료되지 않음: ${completedCount}/${expectedCount}`);
      }
    } else {
      // 멀티 모드: 기존 로직
      const allCompleted = arObjects.every(obj => newQuizStatus[obj.id] === true);
      if (allCompleted) {
        console.log('🎉 모든 문제를 완료했습니다!');

        setTimeout(() => {
          setShowFinalRanking(true);
        }, 1000);
      }
    }
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswers([]);
    setIsQuizComplete(false);
    setShowInteractionModal(false);
  };

  // 오브젝트 네비게이션 함수들
  const handlePreviousObject = async () => {
    if (currentObjectIndex > 0 && !isObjectTransitioning) {
      setIsObjectTransitioning(true);
      
      // 페이드 아웃 애니메이션
      setTimeout(() => {
        setCurrentObjectIndex(prev => prev - 1);
        // 회전값 초기화
        setRotation({ x: 0, y: 0, z: 0 });
        setScale(1);
        
        // 페이드 인 애니메이션
        setTimeout(() => {
          setIsObjectTransitioning(false);
        }, 300);
      }, 300);
    }
  };

  const handleNextObject = async () => {
    if (currentObjectIndex < arObjects.length - 1 && !isObjectTransitioning) {
      setIsObjectTransitioning(true);
      
      // 페이드 아웃 애니메이션
      setTimeout(() => {
        setCurrentObjectIndex(prev => prev + 1);
        // 회전값 초기화
        setRotation({ x: 0, y: 0, z: 0 });
        setScale(1);
        
        // 페이드 인 애니메이션
        setTimeout(() => {
          setIsObjectTransitioning(false);
        }, 300);
      }, 300);
    }
  };

  // 최종 랭킹에서 홈으로 이동
  const handleFinalRankingContinue = () => {
    console.log('최종 랭킹 완료 - 홈으로 이동');
    // 실제로는 navigate 등을 사용할 수 있지만, 여기서는 랭킹 화면만 닫기
    setShowFinalRanking(false);
    // 추가적으로 게임 리셋이나 다른 동작을 수행할 수 있습니다.
  };

  return (
    <GameContainer
      id="game-container"
      $isDragging={isDragging}
      $arObjectVisible={arObjectVisible}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <CameraFeed
        ref={videoRef}
        autoPlay
        playsInline
        muted
      />
      
      <VirtualBackground />
      
      <HiddenCanvas ref={canvasRef} />
      
      {/* Solo 모드가 아니거나 QR 스캔 모드일 때 QR 스캔 오버레이 표시 */}
      {(!isSoloMode || (isSoloMode && gameMode === 'qr-scan')) && (
        <ScanOverlay isVisible={isScanning}>
          <div>
            <ScanBox isScanning={isScanning}>
              <ScanLine isScanning={isScanning} />
            </ScanBox>
            <ScanText>QR 코드를 스캔 중...</ScanText>
          </div>
        </ScanOverlay>
      )}


      {/* Three.js 캔버스 추가 - OBJ 모델용 */}
      <ThreeCanvas
        ref={threeCanvasRef}
        $isVisible={arObjectVisible}
        $shiftLeft={showInteractionModal}
      />

      {/* Solo 모드에서 GLB 로딩 중 오버레이 */}
      {isSoloMode && (
        <LoadingOverlay $isVisible={isLoadingModel}>
          <LoadingSpinner />
          <LoadingText>AR 객체 로딩 중...</LoadingText>
        </LoadingOverlay>
      )}

    {/* CSS 큐브 - OBJ가 없고 arObjectVisible이 true일 때만 표시 */}
    {meshRef.current === null && arObjectVisible && (
      <ARObject
        isVisible={arObjectVisible}
        scale={scale}
        shiftLeft={showInteractionModal}
        style={{
          opacity: isObjectTransitioning ? 0.3 : 1,
          transition: 'opacity 0.3s ease'
        }}
      >
        <Cube rotation={rotation}>
          <FrontFace>
            <FaceArrow>▲</FaceArrow>
            <FaceLabel>FRONT</FaceLabel>
          </FrontFace>
          
          <BackFace>
            <FaceArrow>▼</FaceArrow>
            <FaceLabel>BACK</FaceLabel>
          </BackFace>
          
          <LeftFace>
            <FaceArrow>◄</FaceArrow>
            <FaceLabel>LEFT</FaceLabel>
          </LeftFace>
          
          <RightFace>
            <FaceArrow>►</FaceArrow>
            <FaceLabel>RIGHT</FaceLabel>
          </RightFace>
          
          <TopFace>
            <FaceArrow>⬆</FaceArrow>
            <FaceLabel>TOP</FaceLabel>
          </TopFace>
          
          <BottomFace>
            <FaceArrow>⬇</FaceArrow>
            <FaceLabel>BOTTOM</FaceLabel>
          </BottomFace>
        </Cube>
        
        <Shadow isVisible={arObjectVisible} />

        <RotationDisplay>
          X: {rotation.x}° Y: {rotation.y}° Z: {rotation.z}°
        </RotationDisplay>
        
        {/* 오브젝트 정보 표시 */}
        <div style={{ 
          position: 'absolute', 
          top: '-100px', 
          left: '50%', 
          transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.8)',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '600',
          whiteSpace: 'nowrap',
          zIndex: 10
        }}>
          {arObjects[currentObjectIndex]?.name}
        </div>
      </ARObject>
    )}

{/* 애니메이션 컨트롤 */}
{animations.length > 0 && arObjectVisible && (
  <AnimationControls>
    <div style={{ color: 'white', fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>
      🎬 애니메이션
    </div>

    {/* 재생/정지 버튼 */}
    <AnimationButton onClick={toggleAnimation}>
      {isAnimationPlaying ? <Pause size={16} /> : <Play size={16} />}
      {isAnimationPlaying ? '일시정지' : '재생'}
    </AnimationButton>

    {/* 애니메이션 선택 */}
    {animations.length > 1 && (
      <AnimationSelect
        value={currentAnimationIndex}
        onChange={(e) => changeAnimation(parseInt(e.target.value))}
      >
        {animations.map((anim, index) => (
          <option key={index} value={index}>
            {anim.name || `애니메이션 ${index + 1}`}
          </option>
        ))}
      </AnimationSelect>
    )}
  </AnimationControls>
)}

      {/* 퀴즈 제목 표시 (Solo 모드) */}
      {isSoloMode && quizData?.title && (
        <QuizTitleBanner>
          📝 {quizData.title}
        </QuizTitleBanner>
      )}

      <UserInfoCard>
        <UserInfo>
          <UserName>
            <User size={20} />
            <span>{userName}</span>
          </UserName>
          <UserStats>
            {!isSoloMode && (
              <RankBadge>
                <Trophy size={16} />
                <span>#{userRank}위</span>
              </RankBadge>
            )}
            <ScoreDisplay>
              점수: <span>{userScore.toLocaleString()}</span>
            </ScoreDisplay>
          </UserStats>
        </UserInfo>

        <CaptureButton onClick={captureScreen}>
          <CameraIcon size={16} />
          캡처
        </CaptureButton>
      </UserInfoCard>

      <DragHint $isVisible={arObjectVisible && !isDragging} $isDragging={isDragging}>
        화면을 드래그하여 회전하세요
      </DragHint>

      <DragStatus $isVisible={isDragging}>
        드래그 중... 🔄
      </DragStatus>

      {/* Solo 모드가 아니거나 QR 스캔 모드일 때만 QR 스캔 버튼 표시 */}
      {(!isSoloMode || (isSoloMode && gameMode === 'qr-scan')) && (showQRScanButton || (!arObjectVisible && !isScanning)) && (
        <ScanButton onClick={handleQRScan}>
          <Camera size={24} />
          {showQRScanButton ? '다음 QR 코드 스캔' : 'QR 코드 스캔'}
        </ScanButton>
      )}

      {arObjectVisible && (
        <>
          <ControlPanel>
            <ControlTitle>
              <RotateCw size={16} />
              회전 조작
            </ControlTitle>
            
            <AxisControl>
              <AxisLabel>X축 (상하)</AxisLabel>
              <ButtonRow>
                <RotateButton
                  onClick={() => rotateObject('x', -1)}
                  $color="linear-gradient(135deg, #f472b6 0%, #ec4899 100%)"
                  $hoverColor="linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
                >
                  ↑ X
                </RotateButton>
                <RotateButton
                  onClick={() => rotateObject('x', 1)}
                  $color="linear-gradient(135deg, #f472b6 0%, #ec4899 100%)"
                  $hoverColor="linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
                >
                  ↓ X
                </RotateButton>
              </ButtonRow>
            </AxisControl>

            <AxisControl>
              <AxisLabel>Y축 (좌우)</AxisLabel>
              <ButtonRow>
                <RotateButton
                  onClick={() => rotateObject('y', -1)}
                  $color="linear-gradient(135deg, #4ade80 0%, #22c55e 100%)"
                  $hoverColor="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                >
                  ← Y
                </RotateButton>
                <RotateButton
                  onClick={() => rotateObject('y', 1)}
                  $color="linear-gradient(135deg, #4ade80 0%, #22c55e 100%)"
                  $hoverColor="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                >
                  Y →
                </RotateButton>
              </ButtonRow>
            </AxisControl>

            <AxisControl>
              <AxisLabel>Z축 (회전)</AxisLabel>
              <ButtonRow>
                <RotateButton
                  onClick={() => rotateObject('z', -1)}
                  $color="linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)"
                  $hoverColor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                >
                  ↺ Z
                </RotateButton>
                <RotateButton
                  onClick={() => rotateObject('z', 1)}
                  $color="linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)"
                  $hoverColor="linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)"
                >
                  Z ↻
                </RotateButton>
              </ButtonRow>
            </AxisControl>

            <ResetButton
              onClick={() => setRotation({ x: 0, y: 0, z: 0 })}
            >
              <RotateCcw size={14} style={{ marginRight: '4px' }} />
              회전 리셋
            </ResetButton>
          </ControlPanel>

          <ActionPanel>
            <ActionTitle>크기 조절</ActionTitle>
            <ButtonRow style={{ marginBottom: '12px' }}>
              <ScaleButton onClick={() => scaleObject(-1)}>
                <ZoomOut size={16} />
              </ScaleButton>
              <ScaleButton onClick={() => scaleObject(1)}>
                <ZoomIn size={16} />
              </ScaleButton>
            </ButtonRow>

            <ResetButton
              onClick={() => setScale(1)}
              style={{ marginTop: 0, marginBottom: '16px' }}
            >
              <RotateCcw size={14} style={{ marginRight: '4px' }} />
              크기 리셋
            </ResetButton>

            <QuizButton
              onClick={handleStartQuiz}
              disabled={isCurrentObjectQuizCompleted()}
            >
              <Play size={16} />
              {isCurrentObjectQuizCompleted()
                ? '문제 완료'
                : isSoloMode && quizData
                  ? `문제 풀기` // Solo 모드에서는 문제 개수 표시 안 함
                  : `문제 풀기 (${getCurrentQuestions().length}문제)`
              }
            </QuizButton>
            
            {/* 오브젝트 네비게이션 */}
            <ObjectNavigationContainer>
              <ObjectNavButton 
                onClick={handlePreviousObject}
                disabled={currentObjectIndex === 0 || isObjectTransitioning}
              >
                <ChevronLeft size={16} />
              </ObjectNavButton>
              
              <ObjectInfo>
                <ObjectName>{arObjects[currentObjectIndex]?.name}</ObjectName>
                <ObjectCounter>
                  {isSoloMode && gameMode === 'qr-scan'
                    ? `${scannedObjects.findIndex(id => id === arObjects[currentObjectIndex]?.ar_object_id) + 1} / ${arObjects.length}`
                    : `${currentObjectIndex + 1} / ${arObjects.length}`
                  }
                </ObjectCounter>
              </ObjectInfo>
              
              <ObjectNavButton 
                onClick={handleNextObject}
                disabled={currentObjectIndex === arObjects.length - 1 || isObjectTransitioning}
              >
                <ChevronRight size={16} />
              </ObjectNavButton>
            </ObjectNavigationContainer>
          </ActionPanel>
        </>
      )}

      {/* 상호작용 모달 - 5문제 퀴즈 */}
      <ModalBackdrop $isOpen={showInteractionModal} onClick={resetQuiz}>
        <ModalContent $isOpen={showInteractionModal} onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {isSoloMode && quizData && gameMode === 'qr-scan'
                ? `${arObjects[currentObjectIndex]?.name} (${scannedObjects.findIndex(id => id === arObjects[currentObjectIndex]?.ar_object_id) + 1}/${arObjects.length})`
                : isSoloMode && quizData
                  ? `${arObjects[currentObjectIndex]?.name} (${currentObjectIndex + 1}/${arObjects.length})`
                  : `${arObjects[currentObjectIndex]?.name} 퀴즈 (${currentQuestion + 1}/${getCurrentQuestions().length})`
              }
            </ModalTitle>
            <ModalDescription>
              {isQuizComplete
                ? "퀴즈가 완료되었습니다! 결과를 확인하세요."
                : isSoloMode && quizData
                  ? `${arObjects[currentObjectIndex]?.name}와 관련된 문제를 풀어보세요. 정답을 맞히면 점수를 획득할 수 있습니다.`
                  : `${arObjects[currentObjectIndex]?.name}와 관련된 ${getCurrentQuestions().length}문제를 풀어보세요. 정답을 맞히면 점수를 획득할 수 있습니다.`
              }
            </ModalDescription>
          </ModalHeader>

          <ModalBody>
            {!isQuizComplete ? (
              <>
                {/* 타이머 표시 */}
                {remainingTime !== null && (
                  <TimerDisplay
                    isLow={remainingTime <= 5}
                    isMedium={remainingTime > 5 && remainingTime <= 10}
                  >
                    <span>{remainingTime}초</span>
                  </TimerDisplay>
                )}

                {/* 진행률 표시 */}
                <ProgressBar>
                  <ProgressFill
                    $progress={((currentQuestion + 1) / getCurrentQuestions().length) * 100}
                  />
                </ProgressBar>

                {/* 현재 문제 */}
                <QuestionCard>
                  <QuestionTitle>
                    문제 {currentQuestion + 1}: {getCurrentQuestions()[currentQuestion]?.question}
                  </QuestionTitle>
                  <AnswerOptionsContainer>
                    {getCurrentQuestions()[currentQuestion]?.options.map((option, index) => (
                      <AnswerOption
                        key={index}
                        selected={selectedAnswers[currentQuestion] === index}
                        onClick={() => !isTimeUp && handleAnswerSelect(index)}
                        disabled={isTimeUp}
                        style={{
                          opacity: isTimeUp ? 0.5 : 1,
                          cursor: isTimeUp ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {String.fromCharCode(65 + index)}) {option}
                      </AnswerOption>
                    ))}
                  </AnswerOptionsContainer>
                  {isTimeUp && (
                    <div style={{
                      color: '#ef4444',
                      fontSize: '14px',
                      fontWeight: '600',
                      textAlign: 'center',
                      marginTop: '12px'
                    }}>
                      시간이 초과되었습니다!
                    </div>
                  )}
                </QuestionCard>

                {/* 버튼 영역 */}
                <ButtonContainer>
                  {currentQuestion < getCurrentQuestions().length - 1 ? (
                    <PrimaryButton 
                      onClick={handleNextQuestion}
                      disabled={selectedAnswers[currentQuestion] === undefined}
                    >
                      다음 문제
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton 
                      variant="green"
                      onClick={handleQuizSubmit}
                      disabled={selectedAnswers[currentQuestion] === undefined}
                    >
                      답안 제출
                    </PrimaryButton>
                  )}
                  <SecondaryButton onClick={resetQuiz}>
                    취소
                  </SecondaryButton>
                </ButtonContainer>
              </>
            ) : (
              /* 퀴즈 완료 결과 */
              <>
                <ResultContainer>
                  <CelebrationEmoji>🎉</CelebrationEmoji>
                  <ResultTitle>퀴즈 완료!</ResultTitle>
                </ResultContainer>

                {/* 결과 요약 */}
                <ScoreCard>
                  <ScoreRow>
                    <ScoreLabel>정답 수:</ScoreLabel>
                    <ScoreValue>
                      {selectedAnswers.filter((answer, index) => answer === getCurrentQuestions()[index]?.correct).length} / {getCurrentQuestions().length}
                    </ScoreValue>
                  </ScoreRow>
                  <ScoreRow>
                    <ScoreLabel>획득 점수:</ScoreLabel>
                    <ScoreValue color="#16a34a">+{calculateScore()}점</ScoreValue>
                  </ScoreRow>
                  <ScoreRow>
                    <ScoreLabel>{isSoloMode ? '현재 점수:' : '현재 총점:'}</ScoreLabel>
                    <ScoreValue color="#2563eb">
                      {isSoloMode ? `${currentObjectScore.toLocaleString()}점` : `${userScore.toLocaleString()}점`}
                    </ScoreValue>
                  </ScoreRow>
                </ScoreCard>

                {/* 문제별 결과 */}
                <ResultsList>
                  <ResultsTitle>문제별 결과:</ResultsTitle>
                  {getCurrentQuestions().map((question, index) => {
                    const isCorrect = selectedAnswers[index] === question.correct;
                    let questionScore = 0;

                    if (isCorrect) {
                      // 기본 점수 50점 + 시간 점수 계산
                      const baseScore = 50;
                      const timeLimit = question.timeLimit || 30;
                      const timeTaken = questionTimeTaken[index] || 0;
                      const remainingTime = Math.max(0, timeLimit - timeTaken);
                      const timeScore = Math.floor((remainingTime / timeLimit) * 100);
                      questionScore = baseScore + timeScore;
                    }

                    return (
                      <ResultItem key={index}>
                        <ResultIcon $correct={isCorrect}>
                          {isCorrect ? "✓" : "✗"}
                        </ResultIcon>
                        <span>문제 {index + 1}</span>
                        <span style={{ marginLeft: 'auto' }}>
                          {isCorrect ? `+${questionScore}점` : "0점"}
                        </span>
                      </ResultItem>
                    );
                  })}
                </ResultsList>

                <ButtonContainer>
                  <PrimaryButton onClick={() => {
                    if (isSoloMode && quizData && gameMode === 'qr-scan') {
                      // QR 스캔 모드: 퀴즈 완료 후 QR 스캔 버튼 표시
                      resetQuiz();
                      setShowInteractionModal(false);
                      setArObjectVisible(false); // 오브젝트 숨기기
                      if (scannedObjects.length < arObjects.length) {
                        // 아직 스캔할 오브젝트가 남았으면 QR 스캔 버튼 표시
                        setShowQRScanButton(true);
                      }
                    } else if (isSoloMode && quizData) {
                      // Solo 모드 (일반): 다음 객체로 자동 이동
                      resetQuiz();
                      setShowInteractionModal(false);
                      if (currentObjectIndex < arObjects.length - 1) {
                        // 다음 객체가 있으면 이동
                        setTimeout(() => handleNextObject(), 300);
                      } else {
                        // 모든 객체 완료
                        console.log('🎉 모든 퀴즈 완료!');
                      }
                    } else {
                      // 일반 모드
                      resetQuiz();
                    }
                  }}>
                    {isSoloMode && quizData && gameMode === 'qr-scan' ? '완료' :
                     isSoloMode && quizData && currentObjectIndex < arObjects.length - 1 ? '다음 문제' : '완료'}
                  </PrimaryButton>
                </ButtonContainer>
              </>
            )}
          </ModalBody>
        </ModalContent>
      </ModalBackdrop>

      {/* 최종 랭킹 화면 (Solo 모드에서는 표시 안 함) */}
      {!isSoloMode && showFinalRanking && (
        <FinalRankingBoard
          players={getFinalRankingData()}
          onContinue={handleFinalRankingContinue}
        />
      )}

      {/* Solo 모드: 명예의 전당 */}
      {isSoloMode && showHallOfFame && (
        <HallOfFameContainer>
          <HallOfFameContent>
            <HallOfFameTitle>
              <Trophy size={40} color="#FFD700" />
              <span>명예의 전당</span>
            </HallOfFameTitle>

            {myRanking && (
              <MyRankCard>
                <MyRankBadge>내 순위</MyRankBadge>
                <MyRankInfo>
                  <RankNumber>{myRanking.rank}위</RankNumber>
                  <PlayerName>{myRanking.user_id}</PlayerName>
                  <ScoreText>{myRanking.total_score}점</ScoreText>
                </MyRankInfo>
              </MyRankCard>
            )}

            <RankingListContainer>
              <RankingListTitle>전체 랭킹 (TOP 10)</RankingListTitle>
              <RankingList>
                {rankingData.map((player, index) => {
                  const rank = index + 1;
                  const isMe = player.user_id === userName;
                  const isTop3 = rank <= 3;

                  return (
                    <RankingItem key={index} isMe={isMe} isTop3={isTop3}>
                      <HofRankBadge rank={rank}>
                        {rank <= 3 ? (
                          <Trophy size={20} color={rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32'} />
                        ) : (
                          <span>{rank}</span>
                        )}
                      </HofRankBadge>
                      <PlayerInfo>
                        <PlayerNameText isMe={isMe}>{player.user_id}</PlayerNameText>
                        <PlayerScore>{player.total_score}점</PlayerScore>
                      </PlayerInfo>
                    </RankingItem>
                  );
                })}
              </RankingList>
            </RankingListContainer>

            <CloseButton onClick={() => navigate('/history')}>
              히스토리로 이동
            </CloseButton>
          </HallOfFameContent>
        </HallOfFameContainer>
      )}
    </GameContainer>
  );
}

// 명예의 전당 스타일 컴포넌트
const HallOfFameContainer = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const HallOfFameContent = styled.div`
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 2px solid #FFD700;
  border-radius: 24px;
  padding: 40px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(255, 215, 0, 0.3);
`;

const HallOfFameTitle = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  font-size: 36px;
  font-weight: bold;
  color: #FFD700;
  margin-bottom: 30px;
  text-shadow: 0 0 20px rgba(255, 215, 0, 0.5);
`;

const MyRankCard = styled.div`
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: 2px solid #60a5fa;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
`;

const MyRankBadge = styled.div`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
`;

const MyRankInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  color: white;
`;

const RankNumber = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #FFD700;
  text-shadow: 0 0 10px rgba(255, 215, 0, 0.5);
`;

const PlayerName = styled.div`
  font-size: 24px;
  font-weight: bold;
  flex: 1;
`;

const ScoreText = styled.div`
  font-size: 28px;
  font-weight: bold;
  color: #FFD700;
`;

const RankingListContainer = styled.div`
  margin-bottom: 24px;
`;

const RankingListTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  color: white;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
`;

const RankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const RankingItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px;
  background: ${props =>
    props.isMe ? 'rgba(59, 130, 246, 0.3)' :
    props.isTop3 ? 'rgba(255, 215, 0, 0.1)' :
    'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props =>
    props.isMe ? '#3b82f6' :
    props.isTop3 ? 'rgba(255, 215, 0, 0.3)' :
    'transparent'
  };
  border-radius: 12px;
  transition: all 0.2s ease;

  &:hover {
    background: ${props =>
      props.isMe ? 'rgba(59, 130, 246, 0.4)' :
      props.isTop3 ? 'rgba(255, 215, 0, 0.2)' :
      'rgba(255, 255, 255, 0.1)'
    };
  }
`;

const HofRankBadge = styled.div`
  min-width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: bold;
  color: ${props => props.rank <= 3 ? '#FFD700' : 'white'};
  background: ${props =>
    props.rank === 1 ? 'linear-gradient(135deg, #FFD700, #FFA500)' :
    props.rank === 2 ? 'linear-gradient(135deg, #C0C0C0, #808080)' :
    props.rank === 3 ? 'linear-gradient(135deg, #CD7F32, #8B4513)' :
    'rgba(255, 255, 255, 0.1)'
  };
  border-radius: 12px;
  box-shadow: ${props =>
    props.rank <= 3 ? '0 4px 12px rgba(255, 215, 0, 0.3)' : 'none'
  };
`;

const PlayerInfo = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PlayerNameText = styled.div`
  font-size: 18px;
  font-weight: ${props => props.isMe ? 'bold' : 'normal'};
  color: ${props => props.isMe ? '#60a5fa' : 'white'};
`;

const PlayerScore = styled.div`
  font-size: 20px;
  font-weight: bold;
  color: #FFD700;
`;

const CloseButton = styled.button`
  width: 100%;
  padding: 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  }
`;